import type { ChecklistItemInDto, InspectionTemplateInDto } from 'shared/dtos/in/template';
import { HttpRequestEntity } from 'shared/domain/httpRequest/httpRequestModel';
import {
  broadcastCreatedInspectionTemplate,
  broadcastDeletedInspectionTemplate,
  broadcastInspectionTemplateUploadError,
  broadcastRestoredInspectionTemplate,
  broadcastUpdatedInspectionTemplate,
} from 'serviceWorker/broadcasts/inspectionTemplates';
import { updateDeleted } from 'serviceWorker/db/inspectionTemplates/inspectionTemplates';
import { inspectionTemplateSource } from 'serviceWorker/services/inspectionTemplates/inspectionTemplateSource';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { scopeOfQueuedRequest } from 'serviceWorker/helpers/dataScope';
import { LogLevel } from 'shared/types/logger';
import { neverReached } from 'shared/utils/neverReached';
import { responseErrorOf } from './responseError';

/**
 * Answers a template or checklist item change with its `uniqueId`. The server
 * answers with a part of the template only: the stored templates are synced
 * after every change, and an archived template is flagged at once.
 */
export function broadcastInspectionTemplateResponse(request: HttpRequestEntity, response: unknown): void {
  const { uniqueId, templateId } = request.data ?? {};
  const error = responseErrorOf(response);
  if (error) {
    return broadcastInspectionTemplateUploadError(error, uniqueId);
  }
  inspectionTemplateSource.sync();
  // The server's answer once it carries no error: a template, or the items of a checklist.
  const created = response as InspectionTemplateInDto | ChecklistItemInDto[];

  switch (request.method) {
    case 'POST':
      return broadcastCreatedInspectionTemplate(created, uniqueId);
    case 'PUT':
      return broadcastUpdatedInspectionTemplate(response, uniqueId);
    case 'DELETE': {
      // Only a template archive carries `templateId`; a checklist item removal is synced.
      const archived = templateId
        ? scopeOfQueuedRequest(request).write(() => updateDeleted(templateId, true))
        : Promise.resolve();
      void archived
        .catch((archiveError: unknown) =>
          swLog('Could not flag the archived template; the next sync will fix it', LogLevel.INFO, archiveError, { query: { templateId } })
        )
        .then(() => broadcastDeletedInspectionTemplate(response, uniqueId));
      return;
    }
    case 'GET':
      return broadcastRestoredInspectionTemplate(response, uniqueId);
    default:
      neverReached(request.method);
  }
}
